import axios from 'axios';
import Env from '../env.json';
import { getAuthToken, PREFERENCE_USER_DETAILS } from 'utils/storage.utils';

// Utility function to generate query strings
export const querystring = (query = {}) => {
    if (!query || typeof query !== 'object') {
        return '';
    }
    return Object.entries(query)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');
};

// Create an Axios instance with a base URL
const axiosInstance = axios.create({
    baseURL: Env.BASE_URL,
    headers: { 'Content-Type': 'application/json' },
});

// Add a request interceptor to include the auth token
axiosInstance.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Add a response interceptor to handle errors globally
axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem(PREFERENCE_USER_DETAILS);
            window.location.href = '/';
        }
        return Promise.reject(error);
    }
);

export const getRequest = async (url, params = {}) => {
    const query = querystring(params);
    const response = await axiosInstance.get(`${url}${query ? `?${query}` : ''}`);
    return response.data;
};

export const postRequest = async (url, data = {}) => {
    const response = await axiosInstance.post(url, data);
    return response.data;
};

export const putRequest = async (url, data = {}) => {
    const response = await axiosInstance.put(url, data);
    return response.data;
};

export const patchRequest = async (url, data = {}) => {
    const response = await axiosInstance.patch(url, data);
    return response.data;
};

export const deleteRequest = async (url, params = {}) => {
    const query = querystring(params);
    const response = await axiosInstance.delete(`${url}${query ? `?${query}` : ''}`);
    return response.data;
};

export const uploadFiles = async (url, files, params = {}, axiosConfig = {}) => {
    const formData = generateFormData(files, params);
    const {transformRequest: _ignoreTransform, ...restAxios} = axiosConfig || {};
    // Let the runtime set multipart boundary; a bare `multipart/form-data` header breaks multer/browsers.
    const response = await axiosInstance.post(url, formData, {
        timeout: 600000,
        ...restAxios,
        transformRequest: [
            (data, headers) => {
                if (typeof FormData !== 'undefined' && data instanceof FormData) {
                    if (headers && typeof headers.delete === 'function') {
                        headers.delete('Content-Type');
                    } else if (headers) {
                        delete headers['Content-Type'];
                    }
                }
                return data;
            },
        ],
    });
    return response.data;
};

export const generateFormData = (files, body = {}) => {
    const formData = new FormData();
    if (Array.isArray(files)) {
        files.forEach((file) => formData.append('files', file));
    } else if (files) {
        formData.append('file', files);
    }
    Object.keys(body).forEach((key) => formData.append(key, body[key]));
    return formData;
};

export default axiosInstance;